import { ComboboxController } from '@avanta/shared/components/Combobox';
import { CheckboxController } from '@avanta/shared/components/ui/Checkbox';
import { DatePickerController } from '@avanta/shared/components/ui/DatePicker';
import { Input } from '@avanta/shared/components/ui/Input';
import { SelectController } from '@avanta/shared/components/ui/Select';
import { DEFAULT_TIMEZONE } from '@avanta/shared/constants/common';
import { EMAIL_PATTERN } from '@avanta/shared/constants/patterns';
import { UserType } from '@avanta/shared/constants/users';
import {
  createConfirmPasswordValidator,
  validationsNickname,
  validationsPassword,
} from '@avanta/shared/constants/validations';
import { genderOptions } from '@avanta/shared/types/patients';
import { languageOptions } from '@avanta/shared/utils/common';
import { timezoneOptions, validateAdultBirthDate } from '@avanta/shared/utils/date';
import { TermsAcceptedCheckbox } from './components/TermsAcceptedCheckbox';
import { type SignupFormConfig, type SignupFormData } from './types';

export const initialFormData: SignupFormData = {
  email: '',
  nickname: '',
  password: '',
  confirmPassword: '',
  appLanguage: '',
  dateOfBirth: undefined,
  gender: '',
  enterpriseId: '',
  centerId: '',
  departmentId: '',
  nif: '',
  familyMember: false,
  termsAccepted: false,
  timeZone: DEFAULT_TIMEZONE,
};

export const signupFormConfig: SignupFormConfig = {
  email: {
    label: 'SignupForm.Form.email.label',
    name: 'email',
    type: 'email',
    placeholder: 'SignupForm.Form.email.placeholder',
    required: true,
    autocomplete: 'email',
    pattern: {
      value: EMAIL_PATTERN,
      message: 'SignupForm.Form.email.error.invalid',
    },
    disabled: (formValues, isProfileSetup = true) => Boolean(formValues?.email) && isProfileSetup,
    component: Input,
  },
  nickname: {
    label: 'SignupForm.Form.nickname.label',
    name: 'nickname',
    type: 'text',
    placeholder: 'SignupForm.Form.nickname.placeholder',
    required: true,
    autocomplete: 'off',
    ...validationsNickname,
    component: Input,
  },
  nif: {
    label: 'SignupForm.Form.nifDni.label',
    name: 'nif',
    type: 'text',
    placeholder: 'SignupForm.Form.nifDni.placeholder',
    required: true,
    component: Input,
  },
  familyMember: {
    label: 'SignupForm.familyMember.label',
    name: 'familyMember',
    className: 'size-4',
    component: CheckboxController,
    userTypes: [UserType.EMPLOYEE],
  },
  termsAccepted: {
    label: 'SignupForm.termsAcceptedLabel',
    name: 'termsAccepted',
    className: 'size-4 self-start mt-1',
    component: TermsAcceptedCheckbox,
    required: 'SignupForm.termsAcceptedError',
  },
  password: {
    label: 'SignupForm.Form.password.label',
    name: 'password',
    type: 'password',
    autocomplete: 'new-password',
    placeholder: 'SignupForm.Form.password.placeholder',
    showPassword: true,
    validate: (value: string | number) => validationsPassword.validate?.(value) ?? true,
    component: Input,
  },
  confirmPassword: {
    label: 'SignupForm.Form.confirmPassword.label',
    name: 'confirmPassword',
    type: 'password',
    autocomplete: 'new-password',
    placeholder: 'SignupForm.Form.confirmPassword.placeholder',
    required: true,
    showPassword: true,
    component: Input,
    validate: (value: string | number, formValues?: unknown) =>
      createConfirmPasswordValidator('password', 'SignupForm.Form.password.error.mismatch')(
        value,
        formValues
      ),
  },
  dateOfBirth: {
    label: 'SignupForm.Form.dateOfBirth.label',
    name: 'dateOfBirth',
    placeholder: 'SignupForm.Form.dateOfBirth.placeholder',
    required: true,
    component: DatePickerController,
    validate: validateAdultBirthDate,
  },
  gender: {
    label: 'SignupForm.Form.gender.label',
    name: 'gender',
    placeholder: 'SignupForm.Form.gender.placeholder',
    options: genderOptions,
    required: true,
    component: SelectController,
  },
  enterpriseId: {
    label: 'SignupForm.Form.enterpriseId.label',
    name: 'enterpriseId',
    type: 'text',
    placeholder: 'XXXXXX',
    required: true,
    component: Input,
  },
  centerId: {
    label: 'SignupForm.Form.centerId.label',
    name: 'centerId',
    type: 'text',
    placeholder: 'SignupForm.Form.centerId.placeholder',
    required: true,
    component: SelectController,
    userTypes: [UserType.EMPLOYEE],
  },
  departmentId: {
    label: 'SignupForm.Form.departmentId.label',
    name: 'departmentId',
    type: 'text',
    placeholder: 'SignupForm.Form.departmentId.placeholder',
    required: true,
    component: SelectController,
    disabled: formValues => !formValues.centerId,
    userTypes: [UserType.EMPLOYEE],
  },
  appLanguage: {
    label: 'SignupForm.Form.appLanguage.label',
    name: 'appLanguage',
    placeholder: 'SignupForm.Form.appLanguage.placeholder',
    options: languageOptions,
    required: true,
    component: SelectController,
  },
  timeZone: {
    label: 'SignupForm.Form.timezone.label',
    name: 'timeZone',
    placeholder: 'SignupForm.Form.timezone.placeholder',
    options: timezoneOptions,
    required: true,
    component: ComboboxController,
  },
};

export const patientSignupSteps = {
  1: {
    title: 'Register.Step1.heading',
    fields: ['email', 'nickname', 'nif', 'password', 'confirmPassword'],
  },
  2: {
    title: 'Register.Step2.heading',
    fields: ['dateOfBirth', 'gender', 'appLanguage', 'timeZone', 'termsAccepted'],
  },
} as const;

export const employeeSignupSteps = {
  1: {
    title: 'Register.Step1.heading',
    fields: [
      'email',
      'nickname',
      'familyMember',
      'nif',
      'centerId',
      'departmentId',
      'password',
      'confirmPassword',
    ],
  },
  2: {
    title: 'Register.Step2.heading',
    fields: ['dateOfBirth', 'gender', 'appLanguage', 'timeZone', 'termsAccepted'],
  },
} as const;

export const profileSetupSteps = {
  1: {
    title: 'Register.Step1.heading',
    fields: ['email', 'nickname', 'nif', 'familyMember', 'centerId', 'departmentId'],
  },
  2: {
    title: 'Register.Step2.heading',
    fields: ['password', 'confirmPassword', 'dateOfBirth', 'gender', 'appLanguage', 'timeZone'],
  },
} as const;

export const FIRST_STEP = 1;

export const showOnlyInSignUp = ['centerId', 'departmentId'];

export const getNifPlaceholderKey = (
  isFamilyMember: boolean,
  useEmployeeCodeLabels: boolean
): string => {
  if (isFamilyMember) {
    return useEmployeeCodeLabels
      ? 'SignupForm.Form.nifFamilyEmployeeCode.placeholder'
      : 'SignupForm.Form.nifFamilyDni.placeholder';
  }
  return useEmployeeCodeLabels
    ? 'SignupForm.Form.nifEmployeeCode.placeholder'
    : 'SignupForm.Form.nifDni.placeholder';
};

export const getFieldPlaceholder = ({
  fieldId,
  placeholderKey,
  useEmployeeCodeLabels,
  isFamilyMember,
}: {
  fieldId: string;
  placeholderKey?: string;
  useEmployeeCodeLabels?: boolean;
  isFamilyMember: boolean;
}): string => {
  if (fieldId === signupFormConfig.nif.name) {
    return getNifPlaceholderKey(isFamilyMember, Boolean(useEmployeeCodeLabels));
  }
  if (!placeholderKey) return '';
  return placeholderKey;
};
